import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { archiveCompany, listCompanies, restoreCompany } from '../../api/business/endpoints';
import type { Company, RecordStatus } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate } from '../../components/format';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { StatusChip } from '../../components/StatusChip';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { CompanyDialog } from './CompanyDialog';

type CompanySortKey = 'name' | 'registrationNumber' | 'currency' | 'createdAt';

const VALUE_OF: Record<CompanySortKey, (company: Company) => string | null> = {
  name: (company) => company.name,
  registrationNumber: (company) => company.registrationNumber,
  currency: (company) => company.currency,
  createdAt: (company) => company.createdAt,
};

/** The companies the user can see — the whole list, so it sorts in the browser (Conventions/08 §3). */
export const CompaniesPage = () => {
  const { language, t } = useLanguage();
  const permissions = usePermissions();
  const canManage = permissions.can('companies.manage') && permissions.writable;
  const query = useQuery({ queryKey: ['companies'], queryFn: listCompanies });
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState<RecordStatus | ''>('ACTIVE');
  const [editing, setEditing] = useState<Company | 'new' | null>(null);
  const sort = useSort<CompanySortKey>('name');
  const archive = useAppMutation(archiveCompany, ['companies', 'licence-usage']);
  const restore = useAppMutation(restoreCompany, ['companies', 'licence-usage']);

  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    (query.data ?? []).filter(
      (company) =>
        (status === '' || company.status === status) &&
        (needle === '' ||
          [company.name, company.registrationNumber, company.vatNumber].some((value) =>
            value?.toLocaleLowerCase('sk').includes(needle),
          )),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );

  return (
    <>
      <PageHeader
        title={t('companies.title')}
        subtitle={t('companies.subtitle')}
        action={
          canManage ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
              {t('companies.create')}
            </Button>
          ) : undefined
        }
      />
      <ErrorNotice
        error={query.error ?? archive.error ?? restore.error}
        onRetry={() => void query.refetch()}
      />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <TextField
            select
            size="small"
            label={t('field.status')}
            value={status}
            onChange={(e) => setStatus(e.target.value as RecordStatus | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="ACTIVE">{t('status.ACTIVE')}</MenuItem>
            <MenuItem value="ARCHIVED">{t('status.ARCHIVED')}</MenuItem>
          </TextField>
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="name">
                  {t('field.companyName')}
                </SortableCell>
                <SortableCell sort={sort} column="registrationNumber">
                  {t('field.registrationNumber')}
                </SortableCell>
                <TableCell>{t('field.vatNumber')}</TableCell>
                <SortableCell sort={sort} column="currency">
                  {t('field.currency')}
                </SortableCell>
                <TableCell>{t('field.status')}</TableCell>
                <SortableCell sort={sort} column="createdAt">
                  {t('field.createdAt')}
                </SortableCell>
                {canManage ? <TableCell align="right">{t('common.actions')}</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading ? (
                <TableSkeletonRows columns={canManage ? 7 : 6} />
              ) : (
                rows.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell sx={{ fontWeight: 560 }}>{company.name}</TableCell>
                    <TableCell>{company.registrationNumber ?? t('common.none')}</TableCell>
                    <TableCell>{company.vatNumber ?? t('common.none')}</TableCell>
                    <TableCell>{company.currency}</TableCell>
                    <TableCell>
                      <StatusChip status={company.status} />
                    </TableCell>
                    <TableCell>{formatDate(company.createdAt, language)}</TableCell>
                    {canManage ? (
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => setEditing(company)}>
                            {t('common.edit')}
                          </Button>
                          {company.status === 'ACTIVE' ? (
                            <Button size="small" color="warning" onClick={() => archive.mutate(company.id)}>
                              {t('common.archive')}
                            </Button>
                          ) : (
                            <Button size="small" onClick={() => restore.mutate(company.id)}>
                              {t('common.restore')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && rows.length === 0 ? (
          <EmptyState
            title={t('companies.empty')}
            detail={canManage ? t('companies.emptyManage') : t('companies.emptyAsk')}
            action={
              canManage ? (
                <Button startIcon={<AddIcon />} onClick={() => setEditing('new')}>
                  {t('companies.create')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </Paper>
      {editing ? (
        <CompanyDialog company={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
};
